import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";

import { useAppSelector } from "app/hooks";
import {
  selectCurrentClinic,
  selectCurrentPractice
} from "features/auth/auth-slice";
import useDebounce from "hooks/use-debounce.hook";
import { useQueryPatientsQuery } from "services/patients";
import type { Patient } from "services/types/patients.type";
import { getFullName } from "utils/common";

import type { QuickLauncherAction } from "./quick-launcher.types";

/**
 * Below this the server's full-text search returns too much to be useful, and
 * every keystroke of a name would fire a request. Matches the top bar's own
 * threshold in `components/patient-search`.
 */
export const MIN_PATIENT_SEARCH_LENGTH = 3;

/** The launcher is a jump list, not a search page — a long list defeats the point. */
const PATIENT_RESULT_LIMIT = 5;

/**
 * Deliberately narrower than `PATIENTS_SEARCH_QUERY` in
 * `components/patient-search`: the launcher renders one line of context per
 * row, so requesting tags, notes, photos and clinic/provider objects would be
 * paying for fields nothing displays.
 */
const QUICK_LAUNCHER_PATIENT_QUERY =
  "{id,first_name,middle_name,preferred_name,last_name,birthdate,age_years,phone,is_active}";

const describePatient = (patient: Patient): string =>
  [
    patient.birthdate ? `DOB ${patient.birthdate}` : null,
    patient.phone || null,
    `ID ${patient.id}`,
    patient.is_active === false ? "Deactivated" : null
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * Patient rows for the quick launcher, as ordinary launcher actions so they
 * inherit the panel's keyboard navigation and rendering.
 *
 * These are **not** passed through `filterActions`. The server matches on
 * phone, email and id as well as name, so re-filtering client-side against the
 * visible label would silently drop exactly the rows a phone-number search is
 * for. `patient-search.component.tsx` avoids the same trap with
 * `filterOptions={(options) => options}`.
 */
export default function useQuickLauncherPatients(
  query: string,
  enabled: boolean
): { patientActions: QuickLauncherAction[]; isSearching: boolean } {
  const navigate = useNavigate();
  const currentPractice = useAppSelector(selectCurrentPractice);
  const currentClinic = useAppSelector(selectCurrentClinic);

  // 200ms, the same window the top bar's search uses — two search boxes in the
  // same app that feel different are worse than either choice on its own.
  const { value: debouncedQuery, isDebouncing } = useDebounce(
    query.trim(),
    200
  );

  const isLongEnough = debouncedQuery.length >= MIN_PATIENT_SEARCH_LENGTH;

  const { currentData: patients, isFetching } = useQueryPatientsQuery(
    {
      search: debouncedQuery,
      baseFilter: {
        // Mirrors the top bar so the two searches cannot disagree about which
        // patients exist.
        preferred_clinic: Boolean(currentPractice?.limit_to_current_clinic)
          ? currentClinic?.id
          : undefined
      },
      is_active: currentPractice?.show_deactivated_patients_in_search
        ? undefined
        : true,
      query: QUICK_LAUNCHER_PATIENT_QUERY,
      limit: PATIENT_RESULT_LIMIT
    },
    {
      skip: !enabled || !isLongEnough || !currentPractice || !currentClinic?.id
    }
  );

  const patientActions = useMemo(() => {
    if (!enabled || !isLongEnough || !patients) return [];
    return patients.slice(0, PATIENT_RESULT_LIMIT).map((patient) => ({
      id: `patient:${patient.id}`,
      // One name for every patient row, or the adoption metric fans out per patient.
      telemetryId: "patient:open-chart",
      label:
        getFullName({
          firstName: patient.first_name,
          middleName: patient.middle_name,
          preferredName: patient.preferred_name,
          lastName: patient.last_name
        }) || `Patient ${patient.id}`,
      description: describePatient(patient),
      group: "Patients" as const,
      Icon: PersonOutlineOutlinedIcon,
      perform: () => navigate(`/patients/${patient.id}/`)
    }));
  }, [enabled, isLongEnough, patients, navigate]);

  return {
    patientActions,
    isSearching: enabled && isLongEnough && (isFetching || isDebouncing)
  };
}
